import {
  ApiSuccessResponse,
  BatchCreateCourseRequest,
  BatchCreateResponse,
  Coach,
  CoachStats,
  Course,
  CourseFeedback,
  CourseListResponse,
  CourseQueryParams,
  CourseType,
  CreateCourseRequest,
  CreateLessonPlanRequest,
  EdgeRelation,
  LessonPlanDetail,
  LessonPlanGraphResponse,
  LessonPlanListResponse,
  LessonPlanSummary,
  LockResponse,
  LoginResponse,
  MonthlySummary,
  PackageActivity,
  PackageActivityType,
  PackageDiscountType,
  PackagePricing,
  ReportData,
  SeriesScope,
  SeriesTarget,
  StatusLog,
  Student,
  StudentLevel,
  StudentPackage,
  StudentProgress,
  StudentStats,
  TacticalCapsule,
  TechnicalTag,
  TrainingRecord,
  UpdateCourseRequest,
  UpdateLessonPlanRequest,
  User,
  VenueManager,
  VenuePricing
} from '../types';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// 创建 axios 实例
const api = axios.create({
  baseURL: '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 全局 Toast 处理器注入点：ToastProvider 初始化后调用 setGlobalToastHandler 注入
type ToastFn = (message: string, type?: 'success' | 'error' | 'info') => void;
let globalToastHandler: ToastFn | null = null;
export const setGlobalToastHandler = (fn: ToastFn | null) => {
  globalToastHandler = fn;
};
const notifyError = (msg: string) => {
  if (globalToastHandler) {
    try { globalToastHandler(msg, 'error'); } catch (_) { /* swallow */ }
  }
};

// 请求拦截器 - 添加 token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

// 用 refresh token 换新 access token；失败则清账户跳登录
let refreshInflight: Promise<string | null> | null = null;
async function tryRefreshToken(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  refreshInflight = axios
    .post('/api/auth/refresh', { refreshToken })
    .then((r) => {
      const t = r.data?.token as string | undefined;
      if (t) localStorage.setItem('token', t);
      return t || null;
    })
    .catch(() => null)
    .finally(() => { refreshInflight = null; });
  return refreshInflight;
}

// 响应拦截器 - 处理错误（401 时尝试刷新一次）
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    if (!error.response) {
      console.error('网络连接失败，请检查网络');
      notifyError('网络连接失败，请检查网络');
      return Promise.reject(new Error('网络连接失败，请检查网络'));
    }

    const status = error.response.status;
    const data: any = error.response.data;
    const serverMsg: string =
      (data && typeof data === 'object' && typeof data.error === 'string' && data.error) ||
      (data && typeof data === 'object' && typeof data.message === 'string' && data.message) ||
      '';

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (status === 401 && originalRequest && !originalRequest._retried && originalRequest.url !== '/auth/refresh' && originalRequest.url !== '/auth/login') {
      originalRequest._retried = true;
      const newToken = await tryRefreshToken();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api.request(originalRequest);
      }
      // refresh 也失败，清账户跳登录
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/#/login';
    } else if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/#/login';
    } else if (status === 403) {
      console.error('权限不足');
      notifyError(serverMsg || '权限不足');
    } else if (status === 404) {
      console.error('请求的资源不存在');
      notifyError(serverMsg || '请求的资源不存在');
    } else if (status === 429) {
      notifyError(serverMsg || '请求过于频繁，请稍后再试');
    } else if (status >= 500) {
      console.error('服务器错误');
      notifyError(serverMsg || '服务器错误，请稍后重试');
    }

    // 统一返回一个安全的 Error 对象，确保 .message 始终是字符串
    const msg: string = serverMsg || error.message || '请求失败';
    const safeError: any = new Error(msg);
    safeError.response = error.response;
    return Promise.reject(safeError);
  }
);

// 认证相关 API
export const authAPI = {
  login: (data: { username: string; password: string }) =>
    api.post<LoginResponse>('/auth/login', data),

  getProfile: () =>
    api.get<User>('/auth/profile'),

  changePassword: (data: { old_password: string; new_password: string }) =>
    api.post<ApiSuccessResponse>('/auth/change-password', data),
};

// 课程相关 API
export const courseAPI = {
  getList: (params?: CourseQueryParams) =>
    api.get<CourseListResponse>('/courses', { params }),

  getById: (id: number) =>
    api.get<Course>(`/courses/${id}`),

  create: (data: CreateCourseRequest) =>
    api.post<ApiSuccessResponse & { id: number }>('/courses', data),

  update: (id: number, data: UpdateCourseRequest) =>
    api.put<ApiSuccessResponse>(`/courses/${id}`, data),

  delete: (id: number, scope?: SeriesScope) =>
    api.delete(`/courses/${id}`, { params: scope ? { scope } : undefined }),

  updateStatus: (id: number, status: string) =>
    api.patch(`/courses/${id}/status`, { status }),

  updateAttendance: (courseId: number, attendances: { student_id: number; status: string }[]) =>
    api.patch(`/courses/${courseId}/attendance`, { attendances }),

  updateJiaowu: (id: number, data: { jiaowu_confirmed?: boolean; jiaowu_remark?: string }) =>
    api.patch(`/courses/${id}/jiaowu`, data),

  exportData: () =>
    api.get<{ data: any[] }>('/courses', { params: { export: 'true' } }),

  batchCreate: (data: BatchCreateCourseRequest) =>
    api.post<BatchCreateResponse>('/courses/batch', data),

  updateSeries: (id: number, scope: SeriesScope, data: UpdateCourseRequest) =>
    api.put(`/courses/${id}/series`, data, { params: { scope } }),

  getSeriesTargets: (id: number, scope: SeriesScope) =>
    api.get<SeriesTarget[]>(`/courses/${id}/series-targets`, { params: { scope } })
};

// 教练相关 API
export const coachAPI = {
  getList: (params?: { is_active?: boolean }) =>
    api.get<Coach[]>('/coaches', { params }),

  getById: (id: number) =>
    api.get<Coach>(`/coaches/${id}`),

  // create_account=true 时后端会自动用拼音建账号并在响应里返回 username/password
  create: (data: Partial<Coach> & { create_account?: boolean }) =>
    api.post<{
      message: string;
      coachId: number;
      userId?: number;
      username?: string;
      password?: string;
    }>('/coaches', data),

  update: (id: number, data: Partial<Coach>) =>
    api.put(`/coaches/${id}`, data),

  // 仅更新备注（admin / jiaowu 可调用，后端专用端点）
  updateRemark: (id: number, remark: string) =>
    api.put<ApiSuccessResponse>(`/coaches/${id}/remark`, { remark }),

  delete: (id: number) =>
    api.delete(`/coaches/${id}`),

  resetPassword: (id: number, new_password?: string) =>
    api.post<{ message: string; new_password: string }>(
      `/coaches/${id}/reset-password`,
      new_password ? { new_password } : {}
    ),

  exportData: () =>
    api.get<{ data: any[] }>('/coaches', { params: { export: 'true' } })
};

// 学员相关 API
export const studentAPI = {
  getList: (params?: { is_active?: boolean }) =>
    api.get<Student[]>('/students', { params }),

  getById: (id: number) =>
    api.get<Student>(`/students/${id}`),

  create: (data: Partial<Student>) =>
    api.post<ApiSuccessResponse & { id: number }>('/students', data),

  update: (id: number, data: Partial<Student>) =>
    api.put<ApiSuccessResponse>(`/students/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/students/${id}`),

  updateStatus: (id: number, data: { status: string; remark?: string }) =>
    api.patch<ApiSuccessResponse>(`/students/${id}/status`, data),

  getStatusLogs: (id: number) =>
    api.get<StatusLog[]>(`/students/${id}/status-logs`),

  exportData: () =>
    api.get<{ data: any[] }>('/students', { params: { export: 'true' } })
};

// 统计相关 API
export const statsAPI = {
  getCoachStats: (coachId: number, params?: { year?: string; month?: string }) =>
    api.get<CoachStats>(`/stats/coach/${coachId}`, { params }),

  getStudentStats: (studentId: number, params?: { year?: string; month?: string }) =>
    api.get<StudentStats>(`/stats/student/${studentId}`, { params }),

  getMonthlySummary: (params?: { year?: string }) =>
    api.get<MonthlySummary>('/stats/monthly-summary', { params }),

  getReport: (params?: { year?: string }) =>
    api.get<ReportData>('/stats/report', { params })
};

// 课程类型相关 API
export const courseTypeAPI = {
  getList: () =>
    api.get<CourseType[]>('/course-types'),

  create: (data: { name: string; description?: string; duration_minutes?: number }) =>
    api.post<ApiSuccessResponse & { id: number }>('/course-types', data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/course-types/${id}`)
};

// 课时包相关 API
export const packageAPI = {
  getList: (params?: { student_id?: number; page?: number; limit?: number }) =>
    api.get<StudentPackage[]>('/packages', { params }),

  getById: (id: number) =>
    api.get<StudentPackage>(`/packages/${id}`),

  create: (data: Partial<StudentPackage>) =>
    api.post<ApiSuccessResponse & { id: number }>('/packages', data),

  update: (id: number, data: Partial<StudentPackage>) =>
    api.put<ApiSuccessResponse>(`/packages/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/packages/${id}`),

  consume: (id: number, classes_to_consume?: number) =>
    api.post<ApiSuccessResponse>(`/packages/${id}/consume`, { classes_to_consume: classes_to_consume || 1 }),

  exportData: () =>
    api.get<{ data: StudentPackage[] }>('/packages', { params: { export: 'true' } })
};

// 学员等级相关 API
export const levelAPI = {
  getList: () =>
    api.get<StudentLevel[]>('/levels'),

  create: (data: Partial<StudentLevel>) =>
    api.post<ApiSuccessResponse & { id: number }>('/levels', data),

  update: (id: number, data: Partial<StudentLevel>) =>
    api.put<ApiSuccessResponse>(`/levels/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/levels/${id}`)
};

// 学员进度相关 API
export const progressAPI = {
  getList: (params?: { student_id?: number; page?: number; limit?: number }) =>
    api.get<StudentProgress[]>('/progress', { params }),

  getById: (id: number) =>
    api.get<StudentProgress>(`/progress/${id}`),

  create: (data: Partial<StudentProgress>) =>
    api.post<ApiSuccessResponse & { id: number }>('/progress', data),

  update: (id: number, data: Partial<StudentProgress>) =>
    api.put<ApiSuccessResponse>(`/progress/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/progress/${id}`),

  exportData: () =>
    api.get<{ data: StudentProgress[] }>('/progress', { params: { export: 'true' } })
};

// 课程反馈相关 API
export const feedbackAPI = {
  getList: (params?: { course_id?: number; student_id?: number; page?: number; limit?: number }) =>
    api.get<CourseFeedback[]>('/feedback', { params }),

  getById: (id: number) =>
    api.get<CourseFeedback>(`/feedback/${id}`),

  create: (data: Partial<CourseFeedback>) =>
    api.post<ApiSuccessResponse & { id: number }>('/feedback', data),

  update: (id: number, data: Partial<CourseFeedback>) =>
    api.put<ApiSuccessResponse>(`/feedback/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/feedback/${id}`)
};

// 训练档案相关 API
export const trainingRecordAPI = {
  getList: (params?: { student_id?: number; coach_id?: number; course_id?: number; tag?: string; page?: number; limit?: number }) =>
    api.get<TrainingRecord[]>('/training-records', { params }),

  getById: (id: number) =>
    api.get<TrainingRecord>(`/training-records/${id}`),

  create: (data: { student_id: number; training_date: string; coach_id?: number; course_id?: number | null; training_content?: string; training_effect?: string; strengths?: string; weaknesses?: string; next_plan?: string; notes?: string; videos?: { url: string; title?: string; video_type?: string }[]; tags?: string[] }) =>
    api.post<ApiSuccessResponse & { id: number }>('/training-records', data),

  update: (id: number, data: Partial<TrainingRecord> & { tags?: string[] }) =>
    api.put<ApiSuccessResponse>(`/training-records/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/training-records/${id}`),

  addVideo: (id: number, data: { url: string; title?: string; video_type?: string }) =>
    api.post<ApiSuccessResponse & { id: number; platform: string }>(`/training-records/${id}/videos`, data),

  removeVideo: (recordId: number, videoId: number) =>
    api.delete<ApiSuccessResponse>(`/training-records/${recordId}/videos/${videoId}`),

  getTags: () =>
    api.get<TechnicalTag[]>('/training-records/tags'),
};

export interface TrialConversionStat {
  coach_id: number;
  coach_name: string;
  trial_count: number;
  converted_count: number;
  conversion_rate: number;
}

export const trialConversionAPI = {
  getStats: () => api.get<TrialConversionStat[]>('/stats/trial-conversion')
};

// ===== 视频分析 API =====

export interface VideoAnalysis {
  id: number;
  student_id: number | null;
  student_name: string | null;
  title: string;
  video_path: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  duration_seconds: number | null;
  keyframe_count: number;
  analysis_result: any;
  skeleton_data: any;
  metrics?: any;
  coach_notes?: string | null;
  share_token?: string | null;
  recorded_at?: string | null;  // 视频拍摄日期 YYYY-MM-DD（默认 = 上传日期）
  error_message: string | null;
  frames?: string[];
  history?: Array<{
    id: number;
    title: string;
    completed_at: string;
    metrics: Record<string, number | null>;
  }>;
  created_at: string;
  completed_at: string | null;
}

export const videoAnalysisAPI = {
  upload: (formData: FormData) =>
    api.post<{ message: string; id: number; filename: string }>('/video-analysis/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  analyze: (id: number) =>
    api.post<{ message: string; id: number }>(`/video-analysis/${id}/analyze`),
  getList: (params?: { student_id?: number }) =>
    api.get<VideoAnalysis[]>('/video-analysis', { params }),
  getDetail: (id: number) =>
    api.get<VideoAnalysis>(`/video-analysis/${id}`),
  delete: (id: number) =>
    api.delete(`/video-analysis/${id}`),
  updateCoachNotes: (id: number, coach_notes: string) =>
    api.patch<{ message: string }>(`/video-analysis/${id}`, { coach_notes }),
  updateStudent: (id: number, student_id: number | null) =>
    api.patch<{ message: string }>(`/video-analysis/${id}`, { student_id }),
  updateTitle: (id: number, title: string) =>
    api.patch<{ message: string }>(`/video-analysis/${id}`, { title }),
  updateRecordedAt: (id: number, recorded_at: string | null) =>
    api.patch<{ message: string }>(`/video-analysis/${id}`, { recorded_at }),
  generateShareLink: (id: number) =>
    api.post<{ share_token: string }>(`/video-analysis/${id}/share`),
  revokeShareLink: (id: number) =>
    api.delete(`/video-analysis/${id}/share`),
};

// 公开访问（免登录）
export const videoAnalysisPublicAPI = {
  getByToken: (token: string) =>
    api.get<VideoAnalysis>(`/public/video-analysis/by-token/${token}`),
};

// ===== 教案 API =====

export const lessonPlanAPI = {
  list: (params?: { page?: number; limit?: number; level?: number; tag?: string; q?: string; include_unpublished?: boolean }) =>
    api.get<LessonPlanListResponse>('/lesson-plans', { params }),

  get: (id: number) => api.get<LessonPlanDetail>(`/lesson-plans/${id}`),

  create: (data: CreateLessonPlanRequest) =>
    api.post<{ message: string; id: number }>('/lesson-plans', data),

  update: (id: number, data: UpdateLessonPlanRequest) =>
    api.put<{ message: string; version: number }>(`/lesson-plans/${id}`, data),

  delete: (id: number) => api.delete(`/lesson-plans/${id}`),

  graph: () => api.get<LessonPlanGraphResponse>('/lesson-plans/graph'),

  recommended: (studentId: number) =>
    api.get<{ recommended: LessonPlanSummary[]; also_consider: LessonPlanSummary[]; student_level: StudentLevel | null }>('/lesson-plans/recommended', { params: { student_id: studentId } }),

  acquireLock: (id: number, force = false) =>
    api.post<LockResponse>(`/lesson-plans/${id}/lock`, null, { params: force ? { force: 'true' } : undefined }),

  heartbeat: (id: number) =>
    api.post<{ ok: true; expires_at: string }>(`/lesson-plans/${id}/lock/heartbeat`),

  releaseLock: (id: number) => api.delete(`/lesson-plans/${id}/lock`),

  addVideo: (id: number, data: { url: string; title?: string }) =>
    api.post<{ id: number; platform: string; embed_url?: string }>(`/lesson-plans/${id}/videos`, data),

  removeVideo: (videoId: number) =>
    api.delete(`/lesson-plan-resources/videos/${videoId}`),

  addEdge: (id: number, data: { to_id: number; relation: EdgeRelation }) =>
    api.post<{ id: number }>(`/lesson-plans/${id}/edges`, data),

  removeEdge: (edgeId: number) =>
    api.delete(`/lesson-plan-resources/edges/${edgeId}`),
};

// 战术锦囊相关 API
export const tacticalCapsuleAPI = {
  getList: (params?: { level?: number; tag?: string; q?: string; page?: number; limit?: number }) =>
    api.get<TacticalCapsule[]>('/tactical-capsules', { params }),

  getById: (id: number) =>
    api.get<TacticalCapsule>(`/tactical-capsules/${id}`),

  create: (data: {
    title: string; problem_description: string; solution?: string; level_id?: number;
    is_published?: boolean; drills?: { title: string; description?: string; duration_minutes?: number }[];
    videos?: { url: string; title?: string; video_type?: string }[]; tags?: string[];
  }) => api.post<ApiSuccessResponse & { id: number }>('/tactical-capsules', data),

  update: (id: number, data: {
    title?: string; problem_description?: string; solution?: string; level_id?: number;
    is_published?: number;
    drills?: { title: string; description?: string; duration_minutes?: number }[];
    videos?: { url: string; title?: string; video_type?: string }[]; tags?: string[];
  }) => api.put<ApiSuccessResponse>(`/tactical-capsules/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/tactical-capsules/${id}`),
};

// 场地单价 API
export interface VenueRate {
  id: number;
  location: string;
  hourly_rate: number;
  notes?: string;
}

export const venueAPI = {
  getRates: () =>
    api.get<VenueRate[]>('/venues/rates'),

  updateRate: (data: { location: string; hourly_rate: number; notes?: string }) =>
    api.put<ApiSuccessResponse>('/venues/rates', data),
};

// ── 场地定价管理 ──

export const venuePricingAPI = {
  getList: () =>
    api.get<VenuePricing[]>('/venues/pricing'),

  create: (data: { venue_name: string; court_label?: string; start_time: string; end_time: string; hourly_rate: number; weekend_hourly_rate?: number | null }) =>
    api.post<ApiSuccessResponse & { id: number }>('/venues/pricing', data),

  update: (id: number, data: Partial<Omit<VenuePricing, 'id' | 'created_at' | 'updated_at'>>) =>
    api.put<ApiSuccessResponse>(`/venues/pricing/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/venues/pricing/${id}`),
};

// ── 场地管理员管理 ──

export const venueManagerAPI = {
  getList: () =>
    api.get<VenueManager[]>('/venue-managers'),

  create: (data: { username: string; password: string; venues: string[] }) =>
    api.post<ApiSuccessResponse & { userId: number; username: string; venues: string[] }>('/venue-managers', data),

  update: (userId: number, data: { venues: string[] }) =>
    api.put<ApiSuccessResponse & { venues: string[] }>(`/venue-managers/${userId}`, data),

  delete: (userId: number) =>
    api.delete<ApiSuccessResponse>(`/venue-managers/${userId}`),

  resetPassword: (userId: number, data?: { password?: string }) =>
    api.post<ApiSuccessResponse & { password: string }>(`/venue-managers/${userId}/reset-password`, data || {}),
};

// ── 课时包定价与活动配置 ──

export interface PackagePricingPayload {
  course_type_id?: number;
  classes?: number;
  original_price?: number;
  note?: string | null;
}

export interface PackageActivityPayload {
  name?: string;
  activity_type?: PackageActivityType;
  course_type_id?: number | null;
  discount_type?: PackageDiscountType;
  discount_value?: number;
  min_classes?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  note?: string | null;
}

export const packagePricingAPI = {
  // 定价配置
  getList: () =>
    api.get<PackagePricing[]>('/package-pricing'),

  create: (data: { course_type_id: number; classes: number; original_price: number; note?: string | null }) =>
    api.post<ApiSuccessResponse & { id: number }>('/package-pricing', data),

  update: (id: number, data: PackagePricingPayload) =>
    api.put<ApiSuccessResponse>(`/package-pricing/${id}`, data),

  delete: (id: number) =>
    api.delete<ApiSuccessResponse>(`/package-pricing/${id}`),

  // 活动配置
  getActivities: () =>
    api.get<PackageActivity[]>('/package-pricing/activities'),

  createActivity: (data: {
    name: string; activity_type: PackageActivityType; course_type_id?: number | null;
    discount_type: PackageDiscountType; discount_value: number;
    min_classes?: number | null; start_date?: string | null; end_date?: string | null; note?: string | null;
  }) =>
    api.post<ApiSuccessResponse & { id: number }>('/package-pricing/activities', data),

  updateActivity: (id: number, data: PackageActivityPayload) =>
    api.put<ApiSuccessResponse>(`/package-pricing/activities/${id}`, data),

  deleteActivity: (id: number) =>
    api.delete<ApiSuccessResponse>(`/package-pricing/activities/${id}`),
};

export default api;
